/**
 * Backstage Home — the mobile-only talent-portal hub.
 *
 * Rendered from artist-portal.tsx when useIsMobile() is true. Desktop is left
 * entirely untouched. Reuses the exact React Query keys the desktop portal
 * uses so the cache is shared (no duplicate fetches).
 *
 * Concept: "Backstage" — calm, paper-and-ink luxury. One thing matters most
 * (what's next), everything else recedes into a quiet rhythm of sections.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Loader2,
  CalendarDays,
  Users,
  ClipboardList,
  MapPin,
  Phone,
  Navigation,
  ChevronRight,
  Plus,
  Wallet,
  UserCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { UserProfile } from "@shared/schema";
import {
  greeting,
  countdownLabel,
  longDate,
  shortDate,
  placeLine,
  directionsUrl,
} from "./lib";

interface MyAccessEntry {
  talent: { id: string; name: string };
  canWrite?: boolean;
  isPrimary?: boolean;
}

type EarningsResponse = {
  totals: { paidDollars: number; pendingDollars: number; projectedDollars: number };
};

const usd0 = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

export default function BackstageHome() {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const { data: profile } = useQuery<UserProfile>({ queryKey: ["/api/profile"] });
  const { data: accessList = [], isLoading: talentLoading } = useQuery<MyAccessEntry[]>({
    queryKey: ["/api/talent/my-access"],
  });

  const ownedTalents = useMemo(() => accessList.map((e) => e.talent), [accessList]);
  const [selectedTalentId, setSelectedTalentId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedTalentId && ownedTalents.length > 0) setSelectedTalentId(ownedTalents[0].id);
  }, [ownedTalents, selectedTalentId]);

  const selectedTalent = ownedTalents.find((t) => t.id === selectedTalentId);
  const selectedAccess = accessList.find((e) => e.talent.id === selectedTalentId);
  const canWrite = selectedAccess?.canWrite ?? false;

  const { data: callSheets = [] } = useQuery<any[]>({
    queryKey: ["/api/call-sheets", "byTalent", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(`/api/call-sheets?talentId=${encodeURIComponent(selectedTalentId!)}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("failed to load call sheets");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  const { data: contacts = [] } = useQuery<any[]>({
    queryKey: ["/api/talent-contacts", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(`/api/talent-contacts?talentId=${encodeURIComponent(selectedTalentId!)}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("failed to load contacts");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  const { data: upcoming = [] } = useQuery<any[]>({
    queryKey: ["/api/clients", "upcoming"],
    queryFn: async () => {
      const r = await fetch("/api/clients?status=active&limit=200", { credentials: "include" });
      if (!r.ok) return [];
      const json = await r.json();
      const list: any[] = Array.isArray(json) ? json : json?.items || [];
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const toTs = (s?: string) => {
        if (!s) return NaN;
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
        return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], 12) : new Date(s).getTime();
      };
      return list
        .filter((c) => {
          const t = toTs(c.dateOfEvent);
          return !Number.isNaN(t) && t >= cutoff;
        })
        .sort((a, b) => String(a.dateOfEvent).localeCompare(String(b.dateOfEvent)))
        .slice(0, 12);
    },
  });

  const myShows = useMemo(
    () =>
      upcoming.filter(
        (c) =>
          !selectedTalentId ||
          c.talentId === selectedTalentId ||
          (c.talentIds && c.talentIds.includes(selectedTalentId)),
      ),
    [upcoming, selectedTalentId],
  );

  const { data: earnings } = useQuery<EarningsResponse>({
    queryKey: ["/api/talent/earnings-projection", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(
        `/api/talent/earnings-projection?talentId=${encodeURIComponent(selectedTalentId!)}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("failed");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  const createCallSheet = useMutation({
    mutationFn: async (booking: any) => {
      const r = await apiRequest("POST", "/api/call-sheets", {
        bookingId: booking.id,
        title: `${booking.accountName || "Show"} — ${shortDate(booking.dateOfEvent)}`,
        eventDate: booking.dateOfEvent || null,
        venueName: booking.accountName || null,
        venueAddress: booking.address || null,
      });
      return r.json();
    },
    onSuccess: (sheet: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/call-sheets", "byTalent", selectedTalentId] });
      navigate(`/call-sheet/${sheet.id}`);
    },
    onError: (err: any) =>
      toast({ title: "Couldn't create call sheet", description: err?.message, variant: "destructive" }),
  });

  const firstName = (profile?.firstName as string) || "";
  const next = myShows[0];
  const rest = myShows.slice(1);
  const nextSheet = next ? callSheets.find((cs) => cs.bookingId === next.id) : null;

  if (talentLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="loading-backstage">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (ownedTalents.length === 0) {
    return (
      <div className="min-h-screen bg-background px-5 pt-10">
        <p className="text-sm text-muted-foreground" data-testid="text-no-talent">
          You don&apos;t have a talent profile linked yet. Ask an admin to link you from the Profiles page.
        </p>
      </div>
    );
  }

  const tiles = [
    { id: "contacts", label: "Contacts", icon: Users, href: "/talent/contacts", meta: contacts.length || undefined },
    { id: "callsheets", label: "Call Sheets", icon: ClipboardList, href: "/talent/callsheets", meta: callSheets.length || undefined },
    { id: "calendar", label: "Calendar", icon: CalendarDays, href: "/talent/calendar" },
    { id: "tour", label: "Tour", icon: MapPin, href: "/tour-day" },
    { id: "mynumber", label: "My Number", icon: Phone, href: "/talent/my-number" },
    { id: "profile", label: "Profile", icon: UserCircle, href: "/my-profile" },
  ] as const;

  return (
    <div className="min-h-screen bg-background" data-testid="backstage-home">
      {/* ── Masthead ─────────────────────────────────────────────── */}
      <header className="px-5 pt-8 pb-2">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">Backstage</p>
        <div className="mt-1 flex items-end justify-between gap-3">
          <h1 className="text-[26px] leading-tight font-semibold tracking-tight text-foreground" data-testid="text-greeting">
            {greeting()}{firstName ? `, ${firstName}` : ""}
          </h1>
        </div>
        {ownedTalents.length > 1 && (
          <div className="mt-3">
            <Select value={selectedTalentId ?? ""} onValueChange={setSelectedTalentId}>
              <SelectTrigger className="h-9 w-full rounded-full border-border bg-card" data-testid="select-talent">
                <SelectValue placeholder="Select talent" />
              </SelectTrigger>
              <SelectContent>
                {ownedTalents.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`option-talent-${t.id}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </header>

      {/* ── Up Next hero ─────────────────────────────────────────── */}
      <section className="px-5 pt-3">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground/70 mb-2">Up next</p>
        {next ? (
          <div
            className="rounded-3xl border border-border bg-card p-6 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
            data-testid={`hero-show-${next.id}`}
          >
            <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              {countdownLabel(next.dateOfEvent) || "Scheduled"}
            </span>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight text-foreground leading-snug" data-testid="text-next-show">
              {next.accountName || "Untitled Show"}
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">{longDate(next.dateOfEvent)}</p>
            {placeLine(next.address) && (
              <p className="mt-0.5 text-sm text-muted-foreground/90">{placeLine(next.address)}</p>
            )}
            <div className="mt-5 flex flex-wrap gap-2.5">
              {directionsUrl(next.address) && (
                <a
                  href={directionsUrl(next.address)!}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground active:scale-[0.98] transition-transform"
                  data-testid="button-directions"
                >
                  <Navigation className="h-4 w-4" /> Directions
                </a>
              )}
              {nextSheet ? (
                <button
                  onClick={() => navigate(`/call-sheet/${nextSheet.id}`)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground active:scale-[0.98] transition-transform"
                  data-testid="button-open-callsheet"
                >
                  <ClipboardList className="h-4 w-4" /> Call Sheet
                </button>
              ) : canWrite ? (
                <button
                  onClick={() => createCallSheet.mutate(next)}
                  disabled={createCallSheet.isPending}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground active:scale-[0.98] transition-transform disabled:opacity-60"
                  data-testid="button-create-callsheet"
                >
                  {createCallSheet.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Call Sheet
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-border bg-card/50 p-8 text-center" data-testid="hero-empty">
            <CalendarDays className="mx-auto h-7 w-7 text-muted-foreground/50" />
            <p className="mt-3 text-sm text-muted-foreground">No shows on the calendar yet.</p>
            <p className="text-xs text-muted-foreground/70">When you&apos;re booked, your next date lives here.</p>
          </div>
        )}
      </section>

      {/* ── Quick access ─────────────────────────────────────────── */}
      <section className="px-5 pt-7">
        <div className="grid grid-cols-3 gap-3">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <Link key={t.id} href={t.href}>
                <div
                  className="group flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card py-4 active:scale-[0.97] transition-transform"
                  data-testid={`tile-${t.id}`}
                >
                  <div className="relative">
                    <Icon className="h-6 w-6 text-foreground/80" />
                    {t.meta ? (
                      <span className="absolute -right-3 -top-2 min-w-[18px] rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-[18px] text-primary-foreground">
                        {t.meta}
                      </span>
                    ) : null}
                  </div>
                  <span className="text-xs font-medium text-foreground/90">{t.label}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Money glance ─────────────────────────────────────────── */}
      {earnings && (earnings.totals.projectedDollars > 0 || earnings.totals.paidDollars > 0) && (
        <section className="px-5 pt-7">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground/70 mb-2">This season</p>
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="card-money">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">Projected</span>
              <span className="text-2xl font-semibold tracking-tight tabular-nums text-foreground" data-testid="text-projected">
                {usd0(earnings.totals.projectedDollars)}
              </span>
            </div>
            <div className="mt-3 flex gap-6">
              <div>
                <p className="text-xs text-muted-foreground">Paid</p>
                <p className="text-base font-medium tabular-nums text-primary">{usd0(earnings.totals.paidDollars)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-base font-medium tabular-nums text-foreground/80">{usd0(earnings.totals.pendingDollars)}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Coming up ────────────────────────────────────────────── */}
      {rest.length > 0 && (
        <section className="px-5 pt-7">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground/70 mb-2">Coming up</p>
          <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
            {rest.map((s) => {
              const sheet = callSheets.find((cs) => cs.bookingId === s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => (sheet ? navigate(`/call-sheet/${sheet.id}`) : navigate("/talent/calendar"))}
                  className="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-foreground/[0.03]"
                  data-testid={`row-show-${s.id}`}
                >
                  <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl bg-foreground/[0.05]">
                    <span className="text-[10px] uppercase leading-none text-muted-foreground">
                      {shortDate(s.dateOfEvent).split(",")[0]}
                    </span>
                    <span className="text-sm font-semibold leading-tight text-foreground">
                      {(s.dateOfEvent || "").slice(8, 10) || "—"}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{s.accountName || "Untitled Show"}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {longDate(s.dateOfEvent)}
                      {placeLine(s.address) ? ` · ${placeLine(s.address)}` : ""}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                </button>
              );
            })}
          </div>
        </section>
      )}

      <div className="h-10" />
    </div>
  );
}
